import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanificacion } from '@context/PlanificacionContext';
import { areasService } from '@services/areasService';
import { gradosService } from '@services/gradosService';
import { librosService } from '@services/librosService';
import { BookOpen, GraduationCap, BookMarked, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import Button from '@components/common/Button';
import Loading from '@components/common/Loading';

// Filtro temporal: códigos de áreas habilitadas en la app.
// Cuando se agreguen más áreas, simplemente añadirlas aquí.
const AREAS_HABILITADAS = ['CCSS'];

const SeleccionCursoPage = () => {
  const navigate = useNavigate();
  const { seleccionarCurso, cargarSesiones, area, grado } = usePlanificacion();

  // Estados
  const [areas, setAreas] = useState([]);
  const [grados, setGrados] = useState([]);
  const [selectedArea, setSelectedArea] = useState(area?.id || '');
  const [selectedGrado, setSelectedGrado] = useState(grado?.id || '');
  const [libro, setLibro] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  
  // Estados de carga
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [loadingGrados, setLoadingGrados] = useState(true);
  const [loadingLibro, setLoadingLibro] = useState(false);
  const [loadingSesiones, setLoadingSesiones] = useState(false);
  
  // Estados de error
  const [error, setError] = useState('');

  // Cargar áreas al montar (filtradas por AREAS_HABILITADAS)
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const data = await areasService.getAll();
        setAreas(data.filter(a => AREAS_HABILITADAS.includes(a.codigo)));
      } catch (err) {
        setError('Error al cargar las áreas. Verifica que el backend esté corriendo.');
        console.error(err);
      } finally {
        setLoadingAreas(false);
      }
    };

    fetchAreas();
  }, []);

  // Cargar grados al montar
  useEffect(() => {
    const fetchGrados = async () => {
      try {
        const data = await gradosService.getAll();
        setGrados(data);
      } catch (err) {
        setError('Error al cargar los grados. Verifica que el backend esté corriendo.');
        console.error(err);
      } finally {
        setLoadingGrados(false);
      }
    };

    fetchGrados();
  }, []);

  // Buscar libro cuando se selecciona área y grado
  useEffect(() => {
    const fetchLibro = async () => {
      if (!selectedArea || !selectedGrado) {
        setLibro(null);
        setSesiones([]);
        return;
      }

      setLoadingLibro(true);
      setError('');

      try {
        const libros = await librosService.getByAreaAndGrado(selectedArea, selectedGrado);
        
        if (libros && libros.length > 0) {
          const libroEncontrado = libros[0];
          setLibro(libroEncontrado);
          
          // Cargar sesiones del libro
          setLoadingSesiones(true);
          const sesionesData = await librosService.getSesiones(libroEncontrado.id);
          setSesiones(sesionesData);
          setLoadingSesiones(false);
        } else {
          setLibro(null);
          setSesiones([]);
          setError('No se encontró un libro para esta combinación de área y grado.');
        }
      } catch (err) {
        setError('Error al buscar el libro. Verifica que el backend esté corriendo.');
        console.error(err);
        setLibro(null);
        setSesiones([]);
      } finally {
        setLoadingLibro(false);
      }
    };

    fetchLibro();
  }, [selectedArea, selectedGrado]);

  const handleContinuar = () => {
    if (!libro || !selectedArea || !selectedGrado) return;

    // Buscar objetos completos de área y grado
    const areaObj = areas.find(a => a.id === parseInt(selectedArea));
    const gradoObj = grados.find(g => g.id === parseInt(selectedGrado));

    // Guardar en el context
    seleccionarCurso(areaObj, gradoObj, libro);
    cargarSesiones(sesiones);

    // Navegar a planificación
    navigate('/periodos');
  };

  if (loadingAreas || loadingGrados) {
    return <Loading message="Cargando opciones..." />;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="text-center mb-8">
          <BookOpen className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Selecciona tu curso
          </h1>
          <p className="text-gray-600">
            Elige el área curricular y grado que vas a enseñar
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Selector de Área */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <BookMarked className="w-4 h-4" />
              Área Curricular
            </label>
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="input"
            >
              <option value="">Selecciona un área</option>
              {areas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.nombre}
                </option>
              ))}
            </select>
            {selectedArea && (
              <p className="text-xs text-gray-500 mt-1">
                ✓ Área seleccionada
              </p>
            )}
          </div>

          {/* Selector de Grado */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <GraduationCap className="w-4 h-4" />
              Grado de Secundaria
            </label>
            <select
              value={selectedGrado}
              onChange={(e) => setSelectedGrado(e.target.value)}
              className="input"
            >
              <option value="">Selecciona un grado</option>
              {grados.map((grado) => (
                <option key={grado.id} value={grado.id}>
                  {grado.nombre}
                </option>
              ))}
            </select>
            {selectedGrado && (
              <p className="text-xs text-gray-500 mt-1">
                ✓ Grado seleccionado
              </p>
            )}
          </div>
        </div>

        {/* Indicador de búsqueda */}
        {loadingLibro && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <Loading message="Buscando libro de Ciencias Sociales..." />
          </div>
        )}

        {/* Información del libro encontrado */}
        {libro && !loadingLibro && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  Libro encontrado
                </h3>
                <div className="space-y-1 text-sm text-green-800">
                  <p>
                    <strong>Título:</strong> {libro.titulo}
                  </p>
                  {libro.editorial && (
                    <p>
                      <strong>Editorial:</strong> {libro.editorial}
                    </p>
                  )}
                  {libro.anio && (
                    <p>
                      <strong>Año:</strong> {libro.anio}
                    </p>
                  )}
                  {loadingSesiones ? (
                    <p className="text-green-600">Cargando sesiones...</p>
                  ) : (
                    <p>
                      <strong>Sesiones disponibles:</strong> {sesiones.length} sesiones de clase
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botón de continuar */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900 text-sm"
          >
            ← Volver al inicio
          </button>

          <Button
            onClick={handleContinuar}
            disabled={!libro || !sesiones.length || loadingSesiones}
            icon={ArrowRight}
            size="lg"
          >
            Continuar a planificación
          </Button>
        </div>

        {/* Ayuda */}
        {!selectedArea || !selectedGrado ? (
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Selecciona un área y un grado para buscar automáticamente 
              el libro de Ciencias Sociales correspondiente.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SeleccionCursoPage;
